import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building, Save, Upload, X, MessageSquareText, ShieldAlert } from 'lucide-react';

const BusinessConfig = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSuspension, setSavingSuspension] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    nombre_comercio: '',
    rnc: '',
    telefono: '',
    direccion: '',
    email: '',
    ciudad: '',
    provincia: '',
    logo: '',
    ncf_habilitado: true,
    itbis_habilitado: true,
    itbis_porcentaje: 18.0,
    only_cashiers_open_register: false,
    mensaje_factura: '',
    system_suspended: false,
    system_suspended_title: 'Sistema Suspendido',
    system_suspended_message: 'Servicio suspendido por falta de pago, favor contactar al administrador.'
  });

  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setFormData(response.data);
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('El logo no debe superar los 2MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor seleccione una imagen válida');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData({ ...formData, logo: base64String });
        setLogoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData({ ...formData, logo: '' });
    setLogoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Excluir campos de suspensión de PUT /business-config (se manejan aparte)
      const { system_suspended, system_suspended_title, system_suspended_message, ...rest } = formData;
      await axios.put(`${API}/business-config`, rest);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSuspension = async () => {
    setSavingSuspension(true);
    try {
      const response = await axios.put(`${API}/system/suspension`, {
        system_suspended: !!formData.system_suspended,
        system_suspended_title: formData.system_suspended_title || 'Sistema Suspendido',
        system_suspended_message: formData.system_suspended_message || ''
      });
      setFormData(prev => ({
        ...prev,
        system_suspended: !!response.data.system_suspended,
        system_suspended_title: response.data.system_suspended_title || 'Sistema Suspendido',
        system_suspended_message: response.data.system_suspended_message || ''
      }));
      toast.success(response.data.system_suspended ? 'Sistema suspendido' : 'Sistema reactivado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar suspensión');
    } finally {
      setSavingSuspension(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="business-config-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-800">Configuración del Negocio</h1>
        <p className="text-slate-600 mt-1">Datos que aparecerán en las impresiones</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building size={24} className="text-emerald-500" />
            Información del Comercio
          </CardTitle>
          <CardDescription>
            Complete los datos de su negocio para las facturas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="nombre_comercio">Nombre Comercial *</Label>
                <Input
                  id="nombre_comercio"
                  value={formData.nombre_comercio}
                  onChange={(e) => setFormData({ ...formData, nombre_comercio: e.target.value })}
                  required
                  placeholder="Nombre de su licorería"
                  data-testid="nombre-comercio-input"
                />
              </div>
              <div>
                <Label htmlFor="rnc">RNC *</Label>
                <Input
                  id="rnc"
                  value={formData.rnc}
                  onChange={(e) => setFormData({ ...formData, rnc: e.target.value })}
                  required
                  placeholder="123-45678-9"
                  data-testid="rnc-input"
                />
              </div>
              <div>
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  required
                  placeholder="(809) 555-1234"
                  data-testid="telefono-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@licoreria.com"
                  data-testid="email-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="direccion">Dirección *</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  required
                  placeholder="Calle Principal #123, Sector Centro"
                  data-testid="direccion-input"
                />
              </div>
              <div>
                <Label htmlFor="ciudad">Ciudad</Label>
                <Input
                  id="ciudad"
                  value={formData.ciudad}
                  onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                  placeholder="Santo Domingo"
                  data-testid="ciudad-input"
                />
              </div>
              <div>
                <Label htmlFor="provincia">Provincia</Label>
                <Input
                  id="provincia"
                  value={formData.provincia}
                  onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                  placeholder="Distrito Nacional"
                  data-testid="provincia-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="mensaje_factura" className="flex items-center gap-2">
                  <MessageSquareText size={16} className="text-emerald-500" />
                  Mensaje en pie de página de la factura
                </Label>
                <p className="text-xs text-slate-500 mb-2">
                  Texto que aparecerá al final de cada factura impresa. Puede usar varias líneas.
                </p>
                <Textarea
                  id="mensaje_factura"
                  rows={4}
                  value={formData.mensaje_factura || ''}
                  onChange={(e) => setFormData({ ...formData, mensaje_factura: e.target.value })}
                  placeholder={'Ej:\nGracias por su compra\nNo se aceptan devoluciones después de 5 días'}
                  data-testid="mensaje-factura-input"
                />
              </div>
            </div>

            {/* Logo Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Logo del Comercio</h3>
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="logo">Subir Logo (Opcional)</Label>
                    <p className="text-sm text-slate-600 mb-2">El logo aparecerá en las facturas impresas. Tamaño máximo: 2MB</p>
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="cursor-pointer"
                    />
                  </div>
                  {logoPreview && (
                    <div className="flex flex-col items-center gap-2">
                      <Label>Vista Previa</Label>
                      <div className="relative border-2 border-slate-200 rounded-lg p-2 bg-white">
                        <img 
                          src={logoPreview} 
                          alt="Logo preview" 
                          className="max-w-[150px] max-h-[100px] object-contain"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* NCF and ITBIS Settings */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Configuración Fiscal</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Comprobantes Fiscales (NCF)</Label>
                    <p className="text-sm text-slate-600 mt-1">Habilita o deshabilita el uso de NCF en facturas</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.ncf_habilitado}
                      onChange={(e) => setFormData({ ...formData, ncf_habilitado: e.target.checked })}
                      className="sr-only peer"
                      data-testid="ncf-habilitado-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">ITBIS</Label>
                    <p className="text-sm text-slate-600 mt-1">Habilita o deshabilita el cobro de ITBIS</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.itbis_habilitado}
                      onChange={(e) => setFormData({ ...formData, itbis_habilitado: e.target.checked })}
                      className="sr-only peer"
                      data-testid="itbis-habilitado-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>

                {formData.itbis_habilitado && (
                  <div>
                    <Label htmlFor="itbis_porcentaje">Porcentaje de ITBIS (%)</Label>
                    <Input
                      id="itbis_porcentaje"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.itbis_porcentaje}
                      onChange={(e) => setFormData({ ...formData, itbis_porcentaje: parseFloat(e.target.value) })}
                      data-testid="itbis-porcentaje-input"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Configuración de Cajas */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-slate-800">Configuración de Cajas</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <label className="font-medium text-slate-700">Apertura de cajas solo para Cajeros</label>
                    <p className="text-sm text-slate-500">
                      Si está activado, solo usuarios con rol Cajero podrán ser asignados al abrir una caja
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.only_cashiers_open_register || false}
                      onChange={(e) => setFormData({ ...formData, only_cashiers_open_register: e.target.checked })}
                      className="sr-only peer"
                      data-testid="only-cashiers-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Suspensión del Sistema (solo SuperAdmin) */}
            {isSuperAdmin && (
              <div className="border-t pt-6 mt-6" data-testid="suspension-section">
                <h3 className="text-lg font-semibold mb-4 text-slate-800 flex items-center gap-2">
                  <ShieldAlert size={20} className="text-red-500" />
                  Suspensión del Sistema
                </h3>
                <div className={`rounded-lg p-4 border-2 ${formData.system_suspended ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <label className="font-medium text-slate-800 block">
                        {formData.system_suspended ? 'Sistema suspendido (acceso bloqueado)' : 'Sistema activo'}
                      </label>
                      <p className="text-sm text-slate-600 mt-1">
                        Cuando se activa, todos los usuarios excepto el SuperAdmin quedarán bloqueados de iniciar sesión y de usar el sistema. Los usuarios con sesión abierta serán cerrados automáticamente.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={!!formData.system_suspended}
                        onChange={(e) => setFormData({ ...formData, system_suspended: e.target.checked })}
                        className="sr-only peer"
                        data-testid="system-suspended-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                    </label>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="suspension-title">Título del banner (cabecera)</Label>
                      <Input
                        id="suspension-title"
                        value={formData.system_suspended_title || ''}
                        onChange={(e) => setFormData({ ...formData, system_suspended_title: e.target.value })}
                        placeholder="Sistema Suspendido"
                        data-testid="suspension-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="suspension-message">Mensaje a mostrar al usuario bloqueado</Label>
                      <Textarea
                        id="suspension-message"
                        rows={3}
                        value={formData.system_suspended_message || ''}
                        onChange={(e) => setFormData({ ...formData, system_suspended_message: e.target.value })}
                        placeholder="Servicio suspendido por falta de pago, favor contactar al administrador."
                        data-testid="suspension-message-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button
                      type="button"
                      onClick={handleSaveSuspension}
                      disabled={savingSuspension}
                      className={formData.system_suspended ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-700 hover:bg-slate-800'}
                      data-testid="save-suspension-btn"
                    >
                      {savingSuspension ? 'Guardando...' : (formData.system_suspended ? 'Aplicar Suspensión' : 'Guardar Estado')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-config-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2" size={18} />
                    Guardar Configuración
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessConfig;
